// Growem — app shell. Header (brand + streak + avatar), tab router, global overlays, auto-fit.

import { useEffect, useState } from 'react'
import { useStore } from './state/store'
import { useUI } from './state/ui'
import { TABS, type TabId } from './data/plan'
import { dateLabel } from './domain/dates'
import Auth from './screens/Auth'
import Resumen from './screens/Resumen'
import Sesion from './screens/Sesion'
import Plan from './screens/Plan'
import ComingSoon from './screens/ComingSoon'
import MealSheet from './screens/MealSheet'
import RestOverlay from './screens/RestOverlay'
import Onboarding from './screens/Onboarding'
import Waiting from './screens/Waiting'
import ConfirmSheet from './screens/ConfirmSheet'

const MEAL_PREFILL = '300 g de puré de papa con queso costeño y 4 huevos revueltos'
const HASH_TABS: Record<string, TabId> = {
  '#resumen': 'resumen', '#sesion': 'sesion', '#plan': 'plan', '#bitacora': 'bitacora', '#chat': 'chat',
}

export default function App() {
  const { ready, user, profile, streakNow, undo, redo, undoInfo, redoInfo, today } = useStore()
  const { tab, setTab, mealOpen, openMeal } = useUI()
  // Undo/redo confirmation: the popup names the EXACT action before anything changes.
  const [histConfirm, setHistConfirm] = useState<'undo' | 'redo' | null>(null)
  const histTarget = histConfirm === 'undo' ? undoInfo() : histConfirm === 'redo' ? redoInfo() : null

  // Deep-link on first load: map the URL hash to a tab (and #comida → open meal sheet).
  useEffect(() => {
    const h = window.location.hash
    if (h === '#comida') { setTab('bitacora'); openMeal(MEAL_PREFILL) }
    else if (HASH_TABS[h]) setTab(HASH_TABS[h])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (user && !ready) {
    return <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <span className="disp pulseDot" style={{ color: 'var(--volt)', fontSize: 22, fontWeight: 700 }}>growem<span style={{ color: 'var(--ink-1)' }}>.</span></span>
    </div>
  }
  if (!user) return <Auth />

  // Authed + fully loaded here. Gate the whole tabbed app on plan status.
  if (profile.planStatus === 'onboarding') return <Onboarding />
  if (profile.planStatus === 'pending') return <Waiting />
  // planStatus === 'ready' -> existing tabbed app below

  return (
    <div id="app">
      <header className="hdr">
        <div className="brand disp">growem<span className="dot">.</span></div>
        {/* Undo/redo: dimmed when empty; tapping opens a confirmation naming the exact action. */}
        <button className="counterBtn sec" aria-label="deshacer la última acción" disabled={!undoInfo()}
          onClick={() => undoInfo() && setHistConfirm('undo')}
          style={{ width: 28, height: 28, fontSize: 15, opacity: undoInfo() ? 1 : 0.3 }}>↶</button>
        <button className="counterBtn sec" aria-label="rehacer la acción deshecha" disabled={!redoInfo()}
          onClick={() => redoInfo() && setHistConfirm('redo')}
          style={{ width: 28, height: 28, fontSize: 15, opacity: redoInfo() ? 1 : 0.3 }}>↷</button>
        <div className="flame" title="Racha">
          <svg viewBox="0 0 24 24"><path d="M12 2c1 3-1 4-2 6-1 2 0 4 2 4 1 0 2-1 2-3 2 2 3 4 3 6a5 5 0 0 1-10 0c0-3 2-5 3-7 1-2 2-4 2-6z" /></svg>
          <span className="n">{streakNow()}</span>
        </div>
        <div className="avatar" title="Perfil (próximamente)">{profile.avatarLetter}</div>
      </header>

      <div id="view">
        {tab === 'resumen' && <Resumen />}
        {tab === 'sesion' && <Sesion />}
        {tab === 'plan' && <Plan />}
        {/* Bitácora + Growy temporarily gated behind "muy pronto" while they get polished for
            multi-user (product call 2026-07-31). Data keeps flowing underneath; render-only gate. */}
        {tab === 'bitacora' && <ComingSoon title="bitácora" desc="Tu registro diario completo: comidas, agua, sueño y el detalle de cada sesión, todo en un solo lugar." />}
        {tab === 'chat' && <ComingSoon title="growy" desc="Tu coach con inteligencia artificial: te acompaña, responde tus dudas y te hace seguimiento." />}
      </div>

      <nav className="tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`tab${tab === t.id ? ' on' : ''}`} onClick={() => setTab(t.id)}>
            <svg viewBox="0 0 24 24"><path d={t.icon} /></svg>
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <RestOverlay />
      {mealOpen && <MealSheet />}
      {histConfirm && histTarget && (
        <ConfirmSheet
          title={histConfirm === 'undo' ? 'deshacer' : 'rehacer'}
          body={`${histConfirm === 'undo' ? 'Deshacer' : 'Rehacer'}: ${histTarget.label}${histTarget.day !== today ? ` (del ${dateLabel(histTarget.day)})` : ''}.`}
          actionLabel={histConfirm === 'undo' ? 'Deshacer' : 'Rehacer'}
          onCancel={() => setHistConfirm(null)}
          onConfirm={() => { if (histConfirm === 'undo') undo(); else redo(); setHistConfirm(null) }} />
      )}
    </div>
  )
}
